import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  CheckCheck,
  CheckCircle2,
  Clock,
  Download,
  FileClock,
  FileStack,
  Gauge,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import LeaveBalanceReportTab from "./LeaveBalanceReportTab";
import AttendanceReports from "../attendance/AttendanceReports";
import RegularizationReport from "./RegularizationReport";
import DynamicReports from "./DynamicReports";
import PermissionGate from "../common/PermissionGate";
import PageHeader from "../common/PageHeader";
import TabSelector from "../common/TabSelector";
import StatsCards from "../common/StatsCards";
import Button from "../common/Button";
import PeriodPicker from "./PeriodPicker";
import type { ReportPeriod } from "./reportTabTypes";
import type { StatCardConfig } from "../common/StatsCards";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { useHasPermission } from "../../store/hooks/useRbac";

type ReportTabKey = "leave-balance" | "attendance" | "regularization" | "dynamic";

const TAB_STAT_CARDS: Partial<Record<ReportTabKey, StatCardConfig[]>> = {
  "leave-balance": [
    {
      key: "employees",
      label: "Total Employees",
      icon: Users,
      iconBg: "var(--color-primary-100)",
      iconColor: "var(--color-primary-700)",
    },
    {
      key: "balance",
      label: "Total Leave Balance",
      icon: CalendarDays,
      iconBg: "var(--color-info-light)",
      iconColor: "var(--color-info)",
    },
    {
      key: "avg",
      label: "Avg Balance / Employee",
      icon: TrendingUp,
      iconBg: "var(--color-success-light)",
      iconColor: "var(--color-success-dark)",
    },
    {
      key: "pending",
      label: "Pending Requests",
      icon: FileClock,
      iconBg: "var(--color-warning-light)",
      iconColor: "var(--color-warning-dark)",
    },
  ],
  attendance: [
    {
      key: "totalWorkingDays",
      label: "Total Working Days",
      icon: CalendarDays,
      iconBg: "var(--color-primary-100)",
      iconColor: "var(--color-primary-700)",
    },
    {
      key: "totalPresent",
      label: "Total Present",
      icon: CheckCircle2,
      iconBg: "var(--color-success-light)",
      iconColor: "var(--color-success-dark)",
    },
    {
      key: "totalAbsent",
      label: "Total Absent",
      icon: XCircle,
      iconBg: "var(--color-error-light)",
      iconColor: "var(--color-error-dark)",
    },
    {
      key: "attendanceRate",
      label: "Attendance Rate",
      icon: Gauge,
      iconBg: "var(--color-info-light)",
      iconColor: "var(--color-info)",
      suffix: "%",
    },
  ],
  regularization: [
    {
      key: "totalRequests",
      label: "Total Requests",
      icon: FileStack,
      iconBg: "var(--color-primary-100)",
      iconColor: "var(--color-primary-700)",
    },
    {
      key: "pending",
      label: "Pending",
      icon: Clock,
      iconBg: "var(--color-warning-light)",
      iconColor: "var(--color-warning-dark)",
    },
    {
      key: "approved",
      label: "Approved",
      icon: CheckCheck,
      iconBg: "var(--color-success-light)",
      iconColor: "var(--color-success-dark)",
    },
    {
      key: "rejected",
      label: "Rejected",
      icon: XCircle,
      iconBg: "var(--color-error-light)",
      iconColor: "var(--color-error-dark)",
    },
  ],
};

const NoAccess: React.FC<{ message?: string }> = ({ message }) => (
  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
    {message ?? "You do not have access to this report."}
  </div>
);

const getCurrentPeriod = (): ReportPeriod => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};

const Reports: React.FC = () => {
  const canViewLeaveBalance = useHasPermission([
    PERMISSIONS.REPORTS_LEAVE_BALANCE_VIEW,
    PERMISSIONS.REPORTS_LEAVE_BALANCE_MANAGE,
  ]);
  const canViewAttendance = useHasPermission([
    PERMISSIONS.REPORTS_ATTENDANCE_VIEW,
    PERMISSIONS.REPORTS_ATTENDANCE_MANAGE,
    PERMISSIONS.MY_ATTENDANCE_VIEW,
  ]);
  const canViewRegularization = useHasPermission([
    PERMISSIONS.REPORTS_REGULARIZATION_VIEW,
    PERMISSIONS.REPORTS_REGULARIZATION_MANAGE,
  ]);
  const canViewDynamic = useHasPermission([
    PERMISSIONS.REPORTS_DYNAMIC_VIEW,
    PERMISSIONS.REPORTS_DYNAMIC_MANAGE,
    PERMISSIONS.REPORTS_ATTENDANCE_VIEW,
    PERMISSIONS.REPORTS_LEAVE_BALANCE_VIEW,
    PERMISSIONS.REPORTS_REGULARIZATION_VIEW,
  ]);

  const availableTabs = useMemo(() => {
    const tabs: { k: ReportTabKey; label: string }[] = [];
    if (canViewLeaveBalance) {
      tabs.push({ k: "leave-balance", label: "Leave Balance" });
    }
    if (canViewAttendance) {
      tabs.push({ k: "attendance", label: "Attendance" });
    }
    if (canViewRegularization) {
      tabs.push({ k: "regularization", label: "Regularization" });
    }
    if (canViewDynamic) {
      tabs.push({ k: "dynamic", label: "Dynamic Reports" });
    }
    return tabs;
  }, [canViewLeaveBalance, canViewAttendance, canViewRegularization, canViewDynamic]);

  const [activeTab, setActiveTab] = useState<ReportTabKey>(() => {
    return availableTabs[0]?.k || "attendance";
  });

  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.some((t) => t.k === activeTab)) {
      setActiveTab(availableTabs[0].k);
    }
  }, [availableTabs, activeTab]);

  const [stats, setStats] = useState<Record<string, unknown>>({});
  const [period, setPeriod] = useState<ReportPeriod>(getCurrentPeriod);
  const [exportHandler, setExportHandlerState] = useState<
    (() => void | Promise<void>) | null
  >(null);
  const [exporting, setExporting] = useState(false);

  const setExportHandler = useCallback(
    (handler: (() => void | Promise<void>) | null) => {
      setExportHandlerState(() => handler);
    },
    [],
  );

  const exportHandlerRef = useRef(exportHandler);
  exportHandlerRef.current = exportHandler;

  useEffect(() => {
    setStats({});
    setPeriod(getCurrentPeriod());
  }, [activeTab]);

  const handleExportClick = useCallback(async () => {
    const handler = exportHandlerRef.current;
    if (!handler) return;
    setExporting(true);
    try {
      await handler();
    } catch (err) {
      console.error("Failed to export report:", err);
    } finally {
      setExporting(false);
    }
  }, []);

  const renderTabContent = (): React.ReactNode => {
    switch (activeTab) {
      case "attendance":
        return (
          <PermissionGate
            code={[
              PERMISSIONS.REPORTS_ATTENDANCE_VIEW,
              PERMISSIONS.REPORTS_ATTENDANCE_MANAGE,
              PERMISSIONS.MY_ATTENDANCE_VIEW,
            ]}
            fallback={<NoAccess message="You do not have access to Attendance Reports." />}
          >
            <AttendanceReports
              onStatsChange={setStats}
              registerExport={setExportHandler}
              period={period}
            />
          </PermissionGate>
        );
      case "regularization":
        return (
          <PermissionGate
            code={[
              PERMISSIONS.REPORTS_REGULARIZATION_VIEW,
              PERMISSIONS.REPORTS_REGULARIZATION_MANAGE,
            ]}
            fallback={<NoAccess message="You do not have access to Regularization Reports." />}
          >
            <RegularizationReport
              onStatsChange={setStats}
              registerExport={setExportHandler}
              period={period}
            />
          </PermissionGate>
        );
      case "dynamic":
        return (
          <PermissionGate
            code={[
              PERMISSIONS.REPORTS_DYNAMIC_VIEW,
              PERMISSIONS.REPORTS_DYNAMIC_MANAGE,
              PERMISSIONS.REPORTS_ATTENDANCE_VIEW,
              PERMISSIONS.REPORTS_LEAVE_BALANCE_VIEW,
              PERMISSIONS.REPORTS_REGULARIZATION_VIEW,
            ]}
            fallback={<NoAccess message="You do not have access to Dynamic Reports." />}
          >
            <DynamicReports />
          </PermissionGate>
        );
      case "leave-balance":
      default:
        return (
          <PermissionGate
            code={[
              PERMISSIONS.REPORTS_LEAVE_BALANCE_VIEW,
              PERMISSIONS.REPORTS_LEAVE_BALANCE_MANAGE,
            ]}
            fallback={<NoAccess message="You do not have access to the Leave Balance Report." />}
          >
            <LeaveBalanceReportTab
              onStatsChange={setStats}
              registerExport={setExportHandler}
              period={period}
            />
          </PermissionGate>
        );
    }
  };

  if (availableTabs.length === 0) {
    return (
      <div className="p-3 sm:p-5 lg:p-6 max-w-full mx-auto">
        <PageHeader
          title="Reports"
          description="View leave balance, attendance, regularization and dynamic reports"
          breadcrumbs={[{ label: "Home" }, { label: "Reports" }]}
          className="!mb-5"
        />
        <NoAccess message="You do not have permission to view any reports." />
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        title="Reports"
        description="View leave balance, attendance, regularization and dynamic reports"
        breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "Reports" }]}
        actions={
          activeTab !== "dynamic" ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
              <PeriodPicker
                mode={activeTab === "leave-balance" ? "year" : "month"}
                period={period}
                onChange={setPeriod}
              />
              <Button
                appearance="secondary2"
                size="small"
                onClick={handleExportClick}
                loading={exporting}
                disabled={!exportHandler}
                icon={<Download className="w-4 h-4" />}
              >
                Export
              </Button>
            </div>
          ) : null
        }
      />

      <div className="mb-5 overflow-x-auto no-scrollbar max-w-full pb-1">
        <TabSelector
          size="md"
          tabs={availableTabs}
          active={activeTab}
          onChange={setActiveTab}
        />
      </div>

      {TAB_STAT_CARDS[activeTab] && (
        <div className="mb-5">
          <StatsCards
            variant="simple-icon"
            valueOnTop
            stats={stats}
            cards={TAB_STAT_CARDS[activeTab]}
            gridCols="xl:grid-cols-4"
          />
        </div>
      )}

      <div key={activeTab}>{renderTabContent()}</div>
    </div>
  );
};

export default Reports;